import { API, BACKEND_URL } from '@/config/api';
import React, { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { format } from 'date-fns';

/**
 * Format date to YYYY-MM-DD in local timezone (not UTC)
 */
const formatDateLocal = (date) => {
  return format(date, 'yyyy-MM-dd');
};

/**
 * Custom hook for managing draft save/load functionality
 * 
 * @param {Object} params - Hook parameters
 * @param {Object} params.gridData - Algae grid data
 * @param {Object} params.zooplanktonGridData - Zooplankton grid data
 * @param {Date} params.sampleDate - Sample date
 * @param {string} params.selectedArea - Selected area ID
 * @param {Array} params.customOrganisms - Custom organisms
 * @param {Object} params.colorOverrides - Color overrides
 * @param {Object} params.depthOverrides - Depth overrides
 * @param {Array} params.currentZooplanktonOrganisms - Current zooplankton organisms list
 * @param {function} params.setGridData - Setter for grid data
 * @param {function} params.setZooplanktonGridData - Setter for zooplankton data
 * @param {function} params.setSampleDate - Setter for sample date
 * @param {function} params.setSelectedArea - Setter for selected area
 * @param {function} params.setCustomOrganisms - Setter for custom organisms
 * @param {function} params.setColorOverrides - Setter for color overrides
 * @param {function} params.setDepthOverrides - Setter for depth overrides
 * @param {Array} params.zooplanktonOrganisms - Default zooplankton organisms
 * @param {Array} params.locations - Locations list
 * @returns {Object} Draft management functions and state
 */
export function useDraftManagement({
  gridData,
  zooplanktonGridData,
  sampleDate,
  selectedArea,
  customOrganisms,
  colorOverrides,
  depthOverrides,
  temperatureData,
  currentZooplanktonOrganisms,
  organisms,
  setOrganisms,
  setCurrentZooplanktonOrganisms,
  setGridData,
  setZooplanktonGridData,
  setSampleDate,
  setSelectedArea,
  setCustomOrganisms,
  setColorOverrides,
  setDepthOverrides,
  setTemperatureData,
  zooplanktonOrganisms,
  locations,
  bulkId,
  selectedTemplate,
  setSelectedTemplate,
  setCurrentLocations,
  currentLocations
}) {
  const [draftSaved, setDraftSaved] = useState(false);
  const [lastDraftTime, setLastDraftTime] = useState(null);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const draftIdRef = React.useRef(null); // Sync ref to avoid race conditions
  const savingRef = React.useRef(false);
  const draftLoadedRef = React.useRef(false); // Flag to prevent organism overwriting

  /**
   * Load draft from backend. If specificDraftId is provided, load that specific draft.
   * Otherwise, load the most recent draft.
   */
  const loadDraft = useCallback(async (specificDraftId = null) => {
    // Mark draft loading immediately to prevent fetchOrganisms from running concurrently
    draftLoadedRef.current = true;
    
    try {
      let draft = null;
      
      try {
        const response = await axios.get(`${API}/bulk-drafts`, { withCredentials: true });
        if (response.data && response.data.length > 0) {
          if (specificDraftId) {
            // Load specific draft by ID
            draft = response.data.find(d => d.id === specificDraftId);
          }
          if (!draft) {
            // Fallback: load most recent draft
            const sortedDrafts = response.data.sort((a, b) => {
              const dateA = new Date(a.updated_at || a.created_at);
              const dateB = new Date(b.updated_at || b.created_at);
              return dateB - dateA;
            });
            draft = sortedDrafts[0];
          }
        }
      } catch (error) {
        if (error.response?.status !== 404) {
          console.error('❌ Error fetching drafts from backend:', error);
        }
      }
      
      if (draft) {
        // Support both camelCase and snake_case for backwards compatibility
        const draftGridData = draft.gridData || draft.grid_data || {};
        const draftZooplanktonData = draft.zooplanktonGridData || draft.zooplankton_grid_data || {};
        const draftDateStr = draft.sampleDate || draft.sample_date;
        let draftDate = sampleDate;
        if (draftDateStr) {
          const [y, m, d] = draftDateStr.split('-').map(Number);
          draftDate = new Date(y, m - 1, d);
        }
        const draftArea = draft.selectedArea || draft.selected_area || '';
        const draftCustomOrgs = draft.customOrganisms || draft.custom_organisms || [];
        const draftColorOverrides = draft.colorOverrides || draft.color_overrides || {};
        const draftDepthOverrides = draft.depthOverrides || draft.depth_overrides || {};
        const draftTemperatureData = draft.temperatureData || draft.temperature_data || {};
        
        // Clean up old SUM rows
        const cleanedGridData = { ...draftGridData };
        const keysToRemove = [
          'Svepeflagellater_SVEPEFLAGELLATER SUM',
          'Nåleflagellater_NÅLEFLAGELLATER SUM',
          'Kiselflagellater_KISELFLAGELLATER SUM',
          'Fureflagellater_FUREFLAGELLATER SUM',
          'Dinoflagellater_DINOFLAGELLATER SUM',
          'Diverse_DIVERSE SUM',
          'Cyanobakterier_CYANOBAKTERIER SUM',
          'Total_TOTAL'
        ];
        
        keysToRemove.forEach(key => {
          if (cleanedGridData[key]) {
            delete cleanedGridData[key];
          }
        });
        
        // Set zooplankton data directly — don't clean against currentZooplanktonOrganisms
        // because that state may be stale during initial load (race condition).
        // The draft already has the correct data from when it was saved.
        
        setGridData(cleanedGridData);
        setZooplanktonGridData(draftZooplanktonData);
        setSampleDate(draftDate);
        setSelectedArea(draftArea);
        setCustomOrganisms(draftCustomOrgs);
        setColorOverrides(draftColorOverrides);
        setDepthOverrides(draftDepthOverrides);
        if (setTemperatureData) setTemperatureData(draftTemperatureData);
        
        // Restore the selected template from draft
        const draftTemplate = draft.selected_template || draft.selectedTemplate;
        if (draftTemplate && setSelectedTemplate) {
          setSelectedTemplate(draftTemplate);
        }
        
        // Set locations: from saved current_locations, then grid data, then template
        const savedLocations = draft.current_locations || [];
        if (savedLocations.length > 0) {
          setCurrentLocations(savedLocations);
        } else {
          const firstOrgData = Object.values(cleanedGridData)[0];
          const gridLocations = firstOrgData ? Object.keys(firstOrgData) : [];
          const uniqueGridLocations = [...new Set(gridLocations)];
          
          if (uniqueGridLocations.length > 0) {
            setCurrentLocations(uniqueGridLocations);
          } else if (draftTemplate) {
            try {
              const templatesRes = await axios.get(`${API}/bulk-templates`, { withCredentials: true });
              const tmpl = templatesRes.data?.find(t => t.id === draftTemplate);
              const tmplLocs = tmpl?.config?.locations?.map(l => l.full_name) || [];
              if (tmplLocs.length > 0) {
                setCurrentLocations(tmplLocs);
              }
            } catch {}
          }
        }
        
        // Remove duplicate draftLoadedRef (already set at top of function)
        
        // Load organisms from draft if available (preserves deleted/modified organisms)
        // Set organisms and zooplankton organisms IMMEDIATELY (no delay)
        // Then fetch thresholds in background to enhance the data
        if (draft.organisms && draft.organisms.length > 0) {
          // Set organisms immediately so table renders with correct rows
          setOrganisms(draft.organisms);
        } else {
          // Old draft without organisms — allow fetchOrganisms safety net to run
          draftLoadedRef.current = false;
        }
        if (draft.current_zooplankton_organisms && draft.current_zooplankton_organisms.length > 0) {
          // Mark all zoo organisms from draft as 'from saved data' so the template filter
          // in useOrganismsFetching preserves them even if not in the current template.
          const zooFromDraft = draft.current_zooplankton_organisms.map(z => ({
            ...z,
            __fromSavedData: true
          }));
          setCurrentZooplanktonOrganisms(zooFromDraft);
        }
        
        // Fetch thresholds in background and merge them into organisms (non-blocking)
        (async () => {
          if (draft.organisms && draft.organisms.length > 0) {
            try {
              const algaeRes = await axios.get(`${API}/organisms/algae-structure`, { withCredentials: true });
              const backendOrganisms = algaeRes.data;
              
              // Create a lookup map for thresholds from backend (species + group_thresholds)
              const thresholdMap = {};
              const groupThresholdMap = {};
              Object.entries(backendOrganisms).forEach(([group, groupData]) => {
                const speciesList = Array.isArray(groupData) ? groupData : (groupData?.species || []);
                speciesList.forEach(sp => {
                  const key = `${group}_${sp.species}`;
                  thresholdMap[key] = {
                    threshold_green: sp.threshold_green,
                    threshold_yellow: sp.threshold_yellow,
                    threshold_orange: sp.threshold_orange,
                    threshold_red: sp.threshold_red
                  };
                });
                const gt = groupData?.group_thresholds;
                if (gt) {
                  groupThresholdMap[group] = {
                    threshold_yellow: gt.threshold_yellow,
                    threshold_orange: gt.threshold_orange,
                    threshold_red: gt.threshold_red
                  };
                }
              });
              
              // Merge thresholds into draft organisms
              const organismsWithThresholds = draft.organisms.map(org => {
                const key = `${org.group}_${org.species}`;
                const thresholds = thresholdMap[key];
                if (thresholds) {
                  return { ...org, ...thresholds };
                }
                if (org.isSum && groupThresholdMap[org.group]) {
                  return { ...org, ...groupThresholdMap[org.group] };
                }
                return org;
              });
              
              // Check if template has organisms the draft doesn't
              const tmplId = draft.selected_template || draft.selectedTemplate;
              if (tmplId) {
                try {
                  const templatesRes = await axios.get(`${API}/bulk-templates`, { withCredentials: true });
                  const tmpl = templatesRes.data?.find(t => t.id === tmplId);
                  const tmplOrgs = tmpl?.config?.organisms || [];
                  const draftKeys = new Set(organismsWithThresholds.map(o => `${o.group}_${o.species}`));
                  
                  tmplOrgs.forEach(tmplOrg => {
                    const key = `${tmplOrg.group}_${tmplOrg.species}`;
                    if (!draftKeys.has(key) && thresholdMap[key]) {
                      const orgThresholds = thresholdMap[key];
                      const sumIdx = organismsWithThresholds.findIndex(o => o.group === tmplOrg.group && o.isSum);
                      const insertAt = sumIdx >= 0 ? sumIdx : organismsWithThresholds.length;
                      organismsWithThresholds.splice(insertAt, 0, {
                        group: tmplOrg.group,
                        species: tmplOrg.species,
                        depth: tmplOrg.depth || orgThresholds.depth || '',
                        isSpecies: true,
                        ...orgThresholds
                      });
                    }
                  });
                } catch (e) {
                  // Non-critical
                }
              }
              
              setOrganisms(organismsWithThresholds);
            } catch (error) {
              // Keep draft organisms as-is (already set above)
            }
          }
        })();
        
        setLastDraftTime(new Date(draft.timestamp || draft.updated_at));
        
        // Track the draft ID for future saves
        if (draft.id) {
          draftIdRef.current = draft.id;
          setCurrentDraftId(draft.id);
        }
        
        // Show saved indicator
        setTimeout(() => {
          setDraftSaved(true);
          setTimeout(() => setDraftSaved(false), 2000);
        }, 100);
        
      }
    } catch (error) {
      console.error('❌ Error loading draft:', error);
    }
  }, [currentZooplanktonOrganisms, sampleDate, setColorOverrides, setCustomOrganisms, setDepthOverrides, setGridData, setSampleDate, setSelectedArea, setZooplanktonGridData]);

  /**
   * Save draft to localStorage (demo mode) or backend (real mode)
   */
  const saveDraft = useCallback(async () => {
    try {
      const hasGridData = Object.values(gridData).some(locationData => 
        Object.values(locationData).some(value => value && value.trim())
      );
      const hasZooplanktonData = Object.values(zooplanktonGridData).some(locationData => 
        Object.values(locationData).some(value => value && value.trim())
      );
      
      const hasData = hasGridData || hasZooplanktonData || selectedArea || customOrganisms.length > 0 || Object.keys(colorOverrides).length > 0;
      
      const sampleDateStr = formatDateLocal(sampleDate);
      const todayStr = formatDateLocal(new Date());
      
      if (hasData || sampleDateStr !== todayStr) {
        const draftData = {
          sample_date: sampleDateStr,
          selected_area: selectedArea,
          selected_template: selectedTemplate,
          grid_data: gridData,
          zooplankton_grid_data: zooplanktonGridData,
          custom_organisms: customOrganisms,
          color_overrides: colorOverrides,
          depth_overrides: depthOverrides,
          temperature_data: temperatureData || {},
          organisms: organisms,
          current_zooplankton_organisms: currentZooplanktonOrganisms,
          current_locations: currentLocations || []
        };
        
        if (bulkId) {
          // Editing existing bulk — fetch current data and merge changes
          if (savingRef.current) return;
          savingRef.current = true;
          try {
            const currentBulk = await axios.get(`${API}/bulk-registrations/${bulkId}`, { withCredentials: true });
            const existing = currentBulk.data;
            // Remove internal fields
            delete existing._id;
            delete existing.created_at;
            delete existing.updated_at;
            delete existing.updated_by;
            delete existing.version;
            delete existing.restored_from_version;
            
            await axios.put(`${API}/bulk-registrations/${bulkId}`, {
              ...existing,
              date: sampleDateStr,
              algae_data: gridData,
              zooplankton_data: zooplanktonGridData,
              color_overrides: colorOverrides,
              temperature_data: temperatureData || {},
              current_locations: currentLocations || [],
            }, { withCredentials: true });
          } finally {
            savingRef.current = false;
          }
        } else {
          // Prevent concurrent saves from creating duplicates
          if (savingRef.current) return;
          savingRef.current = true;
          
          try {
            const response = await axios.post(`${API}/bulk-drafts`, {
              ...draftData,
              draft_id: draftIdRef.current || undefined
            }, { withCredentials: true });
            if (response.data.id) {
              draftIdRef.current = response.data.id;
              setCurrentDraftId(response.data.id);
            }
          } finally {
            savingRef.current = false;
          }
        }
        
        setLastDraftTime(new Date());
        setDraftSaved(true);
        setTimeout(() => setDraftSaved(false), 3000);
      }
    } catch (error) {
      console.error('❌ Error saving draft:', error);
    }
  }, [colorOverrides, customOrganisms, depthOverrides, gridData, sampleDate, selectedArea, zooplanktonGridData, bulkId, temperatureData, selectedTemplate, organisms, currentZooplanktonOrganisms, currentLocations]);

  /**
   * Clear draft from storage
   */
  const clearDraft = useCallback(async () => {
    try {
      const idToDelete = draftIdRef.current || currentDraftId;
      if (idToDelete) {
        await axios.delete(`${API}/bulk-drafts/${idToDelete}`, { withCredentials: true });
      }
      
      draftIdRef.current = null;
      setCurrentDraftId(null);
      setDraftSaved(false);
      setLastDraftTime(null);
      setColorOverrides({});
      
      // Reset zooplankton data
      const initial = {};
      zooplanktonOrganisms.forEach(org => {
        const key = `${org.sampleType}_${org.organism}`;
        initial[key] = {};
        locations.forEach(loc => {
          initial[key][loc] = '';
        });
      });
      setZooplanktonGridData(initial);
    } catch (error) {
      console.error('Error clearing draft:', error);
    }
  }, [currentDraftId, locations, setColorOverrides, setZooplanktonGridData, zooplanktonOrganisms]);

  const resetDraftId = useCallback(() => {
    draftIdRef.current = null;
    setCurrentDraftId(null);
  }, []);

  return {
    draftSaved,
    lastDraftTime,
    currentDraftId,
    loadDraft,
    saveDraft,
    clearDraft,
    resetDraftId,
    setDraftSaved,
    setLastDraftTime,
    setCurrentDraftId,
    draftLoadedRef
  };
}
